/**
 * TODAY-CONFLICTS-01 — the office-wide "Needs a look" scan (Seth: "if a
 * dispatch is on a tech, or ISN'T on a tech it should be, alert the office").
 * The board's per-tech recommendation detectors run across TODAY's and
 * TOMORROW's WHOLE boards: requested-tech mismatches, do-not-send violations,
 * skill flags, and calls sitting on a tech who's marked out.
 *
 * Read-only + derived: every row is a fact from notes / skills / availability
 * — same negation-aware detectors the board engine uses, never a guess.
 * Pseudo-employee lanes (holding buckets) are skipped: a "requested Brent"
 * call sitting in the Serv-Agr bucket isn't a conflict, it's just unbooked.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  deriveRequestedTechs,
  deriveExcludedTechs,
  deriveRequiredSkills,
  skillName,
} from "@/lib/recommendations";
import { STATUS_DONE } from "@/lib/types";
import { PSEUDO_CUSTOMERS } from "@/features/board/api";
import { displayCustomerName } from "@/lib/format";

const PSEUDO_EMPNOS = new Set([
  "0000", "0001", "1000", "1001", "1002", "1003", "7777", "8888", "9999",
]);

export interface BoardConflict {
  id: string;
  dayISO: string;
  /** "today", "tomorrow", or the weekday name when the next workday skips a
   * weekend ("Monday"). */
  dayLabel: string;
  dispatch: string;
  custno: string;
  customer: string;
  city: string | null;
  techName: string;
  kind: "excluded" | "out" | "requested" | "skill";
  /** Plain-words description, dispatcher-ready. */
  line: string;
}

/** Severity order for sorting + the panel's tone. */
const KIND_RANK: Record<BoardConflict["kind"], number> = {
  excluded: 0,
  out: 1,
  requested: 2,
  skill: 3,
};

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface RowSlim {
  dispatch: string;
  tech_empno: string;
  custno: string;
  locno: string | null;
  customer_fullname: string | null;
  city: string | null;
  notes: string | null;
  status: string | null;
}

export function useBoardConflicts() {
  return useQuery({
    queryKey: ["board_conflicts"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<BoardConflict[]> => {
      // Today + the next WORKDAY (a Friday scan looks at Monday, not an
      // empty Saturday board).
      const next = new Date();
      do {
        next.setDate(next.getDate() + 1);
      } while (next.getDay() === 0 || next.getDay() === 6);
      const nextLabel =
        next.getTime() - Date.now() < 2 * 86400000
          ? "tomorrow"
          : next.toLocaleDateString(undefined, { weekday: "long" });
      const days: { iso: string; label: string }[] = [
        { iso: toISO(new Date()), label: "today" },
        { iso: toISO(next), label: nextLabel },
      ];

      // Roster (real techs only) — needed for name detection + display names.
      const { data: techRows, error: tErr } = await supabase
        .from("mirror_employee")
        .select("empno, empname, firstname, lastname")
        .eq("dept", "03")
        .eq("inactive", false);
      if (tErr) throw tErr;
      const techs = ((techRows ?? []) as {
        empno: string;
        empname: string | null;
        firstname: string | null;
        lastname: string | null;
      }[]).filter((t) => !PSEUDO_EMPNOS.has(t.empno));
      const roster = techs.map((t) => ({ empno: t.empno, first: t.firstname }));
      const nameOf = (empno: string) => {
        const t = techs.find((x) => x.empno === empno);
        return (
          t?.empname ??
          (`${t?.firstname ?? ""} ${t?.lastname ?? ""}`.trim() || empno)
        );
      };
      const firstNameOf = (empno: string) => nameOf(empno).split(" ")[0];

      // Both boards in parallel.
      const boards = await Promise.all(
        days.map((d) =>
          supabase
            .from("v_route_data")
            .select(
              "dispatch, tech_empno, custno, locno, customer_fullname, city, notes, status",
            )
            .eq("appointment_date", d.iso),
        ),
      );
      for (const b of boards) if (b.error) throw b.error;
      const allRows = boards.map(
        (b) => ((b.data ?? []) as RowSlim[]).filter(
          (r) =>
            !STATUS_DONE.has(r.status ?? "") &&
            !PSEUDO_CUSTOMERS.has(r.custno) &&
            !PSEUDO_EMPNOS.has(r.tech_empno),
        ),
      );

      // Location notes for every custno on either board (requests live on the
      // LOCATION, keyed custno|locno — locno isn't globally unique).
      const custnos = [
        ...new Set(allRows.flat().map((r) => r.custno).filter(Boolean)),
      ];
      const locNotes: Record<string, string> = {};
      for (let i = 0; i < custnos.length; i += 200) {
        const { data, error } = await supabase
          .from("mirror_location")
          .select("custno, locno, notes")
          .in("custno", custnos.slice(i, i + 200));
        if (error) throw error;
        for (const r of (data ?? []) as {
          custno: string;
          locno: string;
          notes: string | null;
        }[]) {
          if (r.notes) locNotes[`${r.custno}|${r.locno}`] = r.notes;
        }
      }

      // Skills (gate: empty load = skip skill checks, never false-flag).
      const { data: skillRows } = await supabase
        .from("tech_skill")
        .select("empno, skill_domain_id")
        .eq("admin_flagged_capable", true);
      const skillsByTech = new Map<string, Set<number>>();
      for (const s of (skillRows ?? []) as {
        empno: string;
        skill_domain_id: number;
      }[]) {
        if (!skillsByTech.has(s.empno)) skillsByTech.set(s.empno, new Set());
        skillsByTech.get(s.empno)!.add(s.skill_domain_id);
      }
      const haveSkills = skillsByTech.size > 0;

      // All-day OUT events across the two days.
      const { data: avail } = await supabase
        .from("tech_availability")
        .select("empno, start_at, end_at, status")
        .eq("all_day", true)
        .neq("status", "denied")
        .lt("start_at", `${days[1].iso}T23:59:59`)
        .gt("end_at", `${days[0].iso}T00:00:00`);
      const outOn = (empno: string, iso: string) =>
        ((avail ?? []) as { empno: string; start_at: string; end_at: string }[]).some(
          (a) =>
            a.empno === empno &&
            a.start_at < `${iso}T23:59:59` &&
            a.end_at > `${iso}T00:00:00`,
        );

      const out: BoardConflict[] = [];
      days.forEach((day, i) => {
        // MULTITECH (Seth 2026-08-28: "Brent is also on the call with him"):
        // a two-man dispatch is two rows — collect the whole crew per dispatch
        // so skill checks judge the CALL, not each rider alone.
        const crewByDispatch = new Map<string, string[]>();
        for (const r of allRows[i]) {
          const arr = crewByDispatch.get(r.dispatch) ?? [];
          if (!arr.includes(r.tech_empno)) arr.push(r.tech_empno);
          crewByDispatch.set(r.dispatch, arr);
        }
        const skillFlagged = new Set<string>();
        for (const r of allRows[i]) {
          const customer = displayCustomerName(null, r.customer_fullname ?? r.custno);
          const techName = firstNameOf(r.tech_empno);
          const combined = `${r.notes ?? ""}\n${locNotes[`${r.custno}|${r.locno ?? ""}`] ?? ""}`;
          const base = {
            dayISO: day.iso,
            dayLabel: day.label,
            dispatch: r.dispatch,
            custno: r.custno,
            customer,
            city: r.city,
            techName: nameOf(r.tech_empno),
          };

          const excluded = deriveExcludedTechs(combined, roster);
          if (excluded.includes(r.tech_empno)) {
            out.push({
              ...base,
              id: `${r.dispatch}-excluded`,
              kind: "excluded",
              line: `the notes ask NOT to send ${techName} — but this call is on him`,
            });
          } else {
            // The customer may accept ANY of several techs ("Jeremy or Camron").
            // Only a conflict if the call is on NONE of the acceptable ones.
            const requestedSet = deriveRequestedTechs(combined, roster).filter(
              (e) => !excluded.includes(e),
            );
            if (requestedSet.length > 0 && !requestedSet.includes(r.tech_empno)) {
              out.push({
                ...base,
                id: `${r.dispatch}-requested`,
                kind: "requested",
                line: `asked for ${requestedSet.map(firstNameOf).join(" or ")} — it's on ${techName}`,
              });
            }
          }

          if (outOn(r.tech_empno, day.iso)) {
            out.push({
              ...base,
              id: `${r.dispatch}-out`,
              kind: "out",
              line: `${techName} is marked out ${day.label}, but this call is still on his board`,
            });
          }

          if (haveSkills && !skillFlagged.has(r.dispatch)) {
            // SKILL-SCOPE-01 (Seth 2026-09-30, the Pam Smith false flag): what
            // KIND of call this is comes from THIS dispatch's own notes only.
            // Location notes are permanent prose — a "gave mini-split estimate
            // 3/10" note from last spring was classifying every later furnace
            // call as a Mini-Split call. Standing requests ("Miles only",
            // "don't send X") still read the location notes above, because
            // those really are standing; the JOB TYPE is per-visit.
            const req = deriveRequiredSkills(r.notes);
            const crew = crewByDispatch.get(r.dispatch) ?? [r.tech_empno];
            // A required skill is COVERED if any tech riding the call has it —
            // Ben + Brent on a boiler is fine when Brent's flagged for boilers.
            const missing = req.filter(
              (id) => !crew.some((e) => skillsByTech.get(e)?.has(id)),
            );
            if (missing.length > 0) {
              skillFlagged.add(r.dispatch); // one flag per call, not per rider
              out.push({
                ...base,
                id: `${r.dispatch}-skill`,
                kind: "skill",
                line:
                  crew.length > 1
                    ? `looks like a ${skillName(missing[0])} call — none of ${crew.map(firstNameOf).join(" + ")} is flagged for it`
                    : `looks like a ${skillName(missing[0])} call — ${techName} isn't flagged for it`,
              });
            }
          }
        }
      });

      // Today first, then by severity.
      out.sort(
        (a, b) =>
          a.dayISO.localeCompare(b.dayISO) ||
          KIND_RANK[a.kind] - KIND_RANK[b.kind],
      );
      return out;
    },
  });
}
